<template>
  <div class="payment-link-step-options">
    <div class="step-description">
      <p>{{ t('Configure opções adicionais do link de pagamento') }}</p>
    </div>

    <!-- Due Date -->
    <div class="option-section">
      <h4>{{ t('Vencimento') }}</h4>
      <input-date
        data-testid="payment-links-step-due-date-input"
        :placeholder="t('Data de vencimento (opcional)')"
        :validation="() => isDueDateValid"
        :required="false"
        elem-id="payment_link_due_date"
        v-model="dueDate">
      </input-date>
      <small class="field-hint">{{ t('Deixe em branco para link sem vencimento') }}</small>
    </div>

    <!-- Description -->
    <div class="option-section">
      <h4>{{ t('Descrição') }}</h4>
      <input-text
        data-testid="payment-links-step-description-input"
        :placeholder="t('Descrição do pagamento')"
        :required="false"
        elem-id="payment_link_description"
        v-model="description">
      </input-text>
      <small class="field-hint">{{ t('Visível para o cliente na página de pagamento') }}</small>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface PaymentLinkFormData {
  due_date?: string | null;
  description?: string;
  [key: string]: unknown;
}

const { t } = useI18n();

const props = defineProps<{
  modelValue: PaymentLinkFormData;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: PaymentLinkFormData): void;
}>();

// Due date with 2-way binding
const dueDate = computed<string | null>({
  get: () => props.modelValue.due_date || null,
  set: (value: string | null) => {
    emit('update:modelValue', { ...props.modelValue, due_date: value });
  }
});

// Description with 2-way binding
const description = computed<string>({
  get: () => props.modelValue.description || '',
  set: (value: string) => {
    emit('update:modelValue', { ...props.modelValue, description: value });
  }
});

// Validation
const isDueDateValid = computed<boolean>(() => {
  if (!dueDate.value) return true; // Optional field
  const date = new Date(dueDate.value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date >= today;
});

const isValid = computed<boolean>(() => {
  return isDueDateValid.value;
});

// Expose validation for wizard
defineExpose({
  isValid
});
</script>

<style scoped>
.payment-link-step-options {
  padding: 1rem 0;
}

.step-description {
  margin-bottom: 1.5rem;
  color: var(--text-color-secondary);
}

.step-description p {
  margin: 0;
}

.option-section {
  margin-bottom: 2rem;
}

.option-section:last-child {
  margin-bottom: 0;
}

.option-section h4 {
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.field-hint {
  display: block;
  color: var(--text-color-secondary);
  font-size: 0.8rem;
  margin-top: 0.5rem;
}
</style>
